<template>
    <b-card no-body border-variant="light" class="mb-2">
        <b-card-header header-tag="header" class="p-0 border-light custom-header" role="tab">
            <b-row class="mb-2">
                <b-col class="col-auto">
                    <b-button :variant="visible ? 'custom-green' : 'outline-secondary'" class="text-uppercase fs-14 fw-600" @click="displayed = !displayed">
                        {{ title }}
                        <span class="float-right ml-2"><b-icon :icon="displayed ? 'chevron-up' : 'chevron-down'" /> </span>
                    </b-button>
                    <b-button v-if="supplementaryAgreement.status.code === 'signed_by_both'" :disabled="pdfLoading" @click="downloadPDF" variant="custom-green" class="ml-2 download-pdf" title="Нажмите, чтобы скачать дополнительное соглашение в формате PDF">
                        <b-icon-file-earmark-pdf />
                    </b-button>
                    <signature-xml-info-button
                        v-if="supplementaryAgreement.supplier_signature || supplementaryAgreement.customer_signature"
                        :title="`Электронная подпись к дополнительному соглашению №${supplementaryAgreement.number}`"
                        :supplier-signature="supplementaryAgreement.supplier_signature"
                        :customer-signature="supplementaryAgreement.customer_signature"
                        :allow-download-xml="purchase.flags.is_participant"
                        :filename="`supplementary_agreement_${supplementaryAgreement.number}_signature_info`"
                        class="ml-2"
                        :mt="0" />
                    <span class="fs-14 fw-600 ml-2">{{ supplementaryAgreement.status.title }}</span>
                </b-col>
            </b-row>
        </b-card-header>
        <b-collapse v-model="displayed" :id="id" accordion="supplementary-agreements-accordion" role="tabpanel">
            <b-card-body class="py-0 px-2">
                <text-row-price :label-cols-lg="4" :value="supplementaryAgreement.price" compact label="Общая стоимость" />
                <text-row-date :label-cols-lg="4" :value="supplementaryAgreement.supplementary_agreement_date" compact label="Дата заключения дополнительного соглашения" />
                <fl44-contract-supplementary-agreement-view-files-table :supplementary-agreement="supplementaryAgreement" />
                <fl44-contract-supplementary-agreement-regular-view-specifications-table :items="supplementaryAgreement.deliverables" :total-cost="supplementaryAgreement.price" />
                <text-row-fl44-old-disagreement-table v-if="supplementaryAgreement.supplementary_agreement_disagreements && supplementaryAgreement.supplementary_agreement_disagreements.length > 0" :disagreements="supplementaryAgreement.supplementary_agreement_disagreements">
                    <template #title>
                        <h5 class="grey-color mt-3 mb-1 main_text font-weight-bold">Разногласия по дополнительному соглашению</h5>
                    </template>
                </text-row-fl44-old-disagreement-table>
                <template v-if="supplementaryAgreement.status.code === 'signed_by_both' && purchase.flags.is_customer && limitsRequired && supplementaryAgreement.limits && supplementaryAgreement.limits.length > 0">
                    <limits-editable-table
                        v-if="purchase.status_enum.id === 'contract:active' && isLimitsEdit"
                        v-model="form.limits"
                        :total-cost="supplementaryAgreement.price"
                        @validate="limitsValidate"
                        :limit-year="purchase.purchase_limit_year"
                        :order-type="{ id: purchase.order_type_id, name: purchase.order_type_name }"
                        purchase-type="fl44_external"
                        :purchase-id="purchase.id">
                        <template #title>
                            <h5 class="grey-color fw-600">Измените лимиты</h5>
                        </template>
                    </limits-editable-table>
                    <limits-table v-else :items="supplementaryAgreement.limits">
                        <template #title>
                            <h5 class="grey-color fw-600">Лимиты по КБК</h5>
                        </template>
                    </limits-table>
                    <template v-if="purchase.status_enum.id === 'contract:active' && purchase.contract.last_active_supplementary_agreement_id === supplementaryAgreement.id">
                        <template v-if="isLimitsEdit">
                            <b-overlay :show="isLoading" class="d-inline-block" opacity="0.6" rounded spinner-small spinner-variant="primary">
                                <b-button variant="custom-outline-secondary" class="text-uppercase mt-1 mb-3" @click="isLimitsEdit = false">Отмена</b-button>
                                <b-button variant="custom-green" :disabled="!isLimitsValid" class="text-uppercase mt-1 mb-3 ml-2" @click="updateLimits">Сохранить</b-button>
                            </b-overlay>
                        </template>
                        <template v-else>
                            <b-button variant="custom-green" class="text-uppercase mt-1 mb-3" @click="editLimits">Изменить лимиты</b-button>
                        </template>
                    </template>
                </template>
                <text-row-supplier-accounting v-if="supplementaryAgreement.status.code === 'signed_by_both' && purchase.flags.is_customer && purchase.contract && supplementaryAgreement.supplier_accounting" v-model="supplementaryAgreement.supplier_accounting">
                    <template #title>
                        <h5 class="grey-color ml-1 mt-3 mb-1 main_text font-weight-bold">Банковские реквизиты поставщика</h5>
                    </template>
                </text-row-supplier-accounting>
                <private-fl44-contract-supplementary-agreement-supplier-block
                    v-if="purchase.contract.latest_supplementary_agreement_id === supplementaryAgreement.id && $store.getters.isLoggedIn && purchase.flags.is_supplier && purchase.actions.supplier.supplementary_agreement !== 'disallow'"
                    :supplementary-agreement="supplementaryAgreement"
                    :purchase="purchase"
                    @refresh="$emit('refresh')" />
                <private-fl44-contract-supplementary-agreement-customer-block
                    :contractPriceTotal="pivc.purchaseitem.contract.price_total"
                    v-if="purchase.contract.latest_supplementary_agreement_id === supplementaryAgreement.id && $store.getters.isLoggedIn && purchase.flags.is_customer && purchase.actions.customer.supplementary_agreement !== 'disallow'"
                    :purchase="purchase"
                    :supplementary-agreement="supplementaryAgreement"
                    @refresh="$emit('refresh')" />
            </b-card-body>
        </b-collapse>
    </b-card>
</template>

<script>
import cloneDeep from 'lodash.clonedeep';
import Config from '@core/js/ddriven/application/config/Config';
import Fl44ContractSupplementaryAgreementRegularViewSpecificationsTable from './fl44-contract-supplementary-agreement-regular-view-specifications-table.vue';
import Fl44ContractSupplementaryAgreementViewFilesTable from '@/components/products/fl44/partials/fl44-contract-supplementary-agreement-view-files-table.vue';
import PrivateFl44ContractSupplementaryAgreementSupplierBlock from '@/components/products/fl44/partials/private-fl44-contract-supplementary-agreement-supplier-block.vue';
import PrivateFl44ContractSupplementaryAgreementCustomerBlock from '@/components/products/fl44/partials/private-fl44-contract-supplementary-agreement-customer-block.vue';
import TextRowSupplierAccounting from '@/components/common/form-rows/text-row-supplier-accounting.vue';
import SignatureXmlInfoButton from '@/components/common/signature-xml-info-button.vue';
import FileDownload from 'js-file-download';
import TextRowFl44OldDisagreementTable from '@/components/common/form-rows/text-row-fl44-old-disagreement-table.vue';

export default {
    name: 'fl44-contract-supplementary-agreement-regular-item',
    components: {
        SignatureXmlInfoButton,
        TextRowSupplierAccounting,
        TextRowFl44OldDisagreementTable,
        PrivateFl44ContractSupplementaryAgreementCustomerBlock,
        PrivateFl44ContractSupplementaryAgreementSupplierBlock,
        Fl44ContractSupplementaryAgreementViewFilesTable,
        Fl44ContractSupplementaryAgreementRegularViewSpecificationsTable,
    },
    props: {
        supplementaryAgreement: {
            type: Object,
            required: true
        },
        purchase: {
            type: Object,
            required: true
        },
        visible: {
            type: Boolean,
            default: false
        },
        pivc: {
            type: Object
        }
    },
    data() {
        return {
            displayed: this.visible,
            isLoading: false,
            isLimitsEdit: false,
            isLimitsValid: false,
            pdfLoading: false,
            form: {
                limits: []
            }
        };
    },
    methods: {
        limitsValidate(value) {
            this.isLimitsValid = !value;
        },
        editLimits() {
            this.form.limits = cloneDeep(this.supplementaryAgreement.limits);
            this.isLimitsEdit = true;
        },
        async updateLimits() {
            const form = {};
            form.limits = this.form.limits.map((item) => {
                return {
                    id: item.id,
                    year: item.year,
                    amount_assigned: item.amount_assigned
                };
            });
            this.isLoading = true;
            this.$api.personal.fl44
                .updateSupplementaryAgreementLimits(this.purchase.id, this.supplementaryAgreement.id, form.limits)
                .then(() => {
                    this.isLimitsEdit = false;
                    this.$emit('refresh');
                })
                .catch(() => {})
                .finally(() => {
                    this.isLoading = false;
                });
        },
        downloadPDF() {
            this.pdfLoading = true;
            this.$api.products.fl44.purchases
                .downloadPurchaseContractSupplementaryAgreementPdf(this.purchase.id, this.supplementaryAgreement.id)
                .then((response) => {
                    const regExpFilename = /filename="(?<filename>.*)"/;
                    const regExpFilename1 = /filename=(?<filename>.*)/;
                    const fileName = regExpFilename.exec(response['headers']['content-disposition'])?.groups?.filename ?? regExpFilename1.exec(response['headers']['content-disposition'])?.groups?.filename;
                    FileDownload(response.data, fileName);
                })
                .catch((error) => {
                    this.showError(error);
                })
                .finally(() => {
                    this.pdfLoading = false;
                });
        }
    },
    computed: {
        id() {
            return 'supplementary_agreement_' + this._uid;
        },
        Config() {
            return Config;
        },
        limitsRequired() {
            return this.features.kbk_limits && !this.purchase.without_limits;
        },
        features() {
            return Config.get('theme.features');
        },
        title() {
            return `${this.supplementaryAgreement.type.title} №${this.supplementaryAgreement.number}`;
        }
    }
};
</script>

<style scoped>
.custom-header {
    background-color: var(--greyBg-color) !important;
}
.download-pdf {
    width: 35px !important;
    min-width: 35px !important;
    padding-left: 8px !important;
}
</style>
